import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { GridSortChangeEvent } from '@progress/kendo-react-grid';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import Loading from '@components-clean/core/loading/loading';
import { Signatory } from '@interfaces/report-templates.interface';
import DeleteSignatoryDialog from '@pages-clean/shared/manage-signatories/delete-signatory-dialog';
import { signatoriesGridColumnHelper } from '@pages-clean/shared/manage-signatories/grid-column-helper';
import { MANAGE_SIGNATORIES_GRID_COLUMNS } from '@pages-clean/shared/manage-signatories/grid-columns';
import ManageSignatoryDialog from '@pages-clean/shared/manage-signatories/manage-signatory-dialog';
import { mapApplicationArea, mapService } from '@utils-clean/grid-download-audit-helper';
import { AuditDataType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean, useLocalState, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';
import { useGetSignatoriesList } from '@apis/bff-api/report-templates';

interface IManageSignatoriesProps {
    module: number;
}

const ManageSignatories = ({ module }: IManageSignatoriesProps) => {
    const { t } = useTranslation(['common', 'cm']);
    const { state: localState } = useLocalState();

    const customCells = useGridCustomCell();

    const studyId = localState?.selectedStudy?.id as string;
    const studyName = localState?.selectedStudy?.shortName;
    const user = useAuthorization();

    const { data: prefixesData, loading: isLoadingPrefixes } = useQuery<auditRecordIdPrefixes>(
        AUDIT_RECORD_ID_PREFIXES,
        {
            fetchPolicy: 'cache-first',
        },
    );

    const canEdit = !!user?.canWrite?.({
        module:
            module === Constants.Modules.RAM
                ? Constants.Modules.RAM
                : module === Constants.Modules.SM
                  ? Constants.Modules.SM
                  : module === Constants.Modules.CM
                    ? Constants.Modules.CM
                    : Constants.Modules.CM2,
        element:
            module === Constants.Modules.RAM
                ? Constants.DataElements.RiskAssessment
                : module === Constants.Modules.SM
                  ? Constants.DataElements.SubjectMonitoring
                  : module === Constants.Modules.CM
                    ? Constants.DataElements.CentralMonitoring
                    : Constants.DataElements.CentralMonitoringDefault,
        permission: Permissions.ReadWrite,
    });

    const [selectedSignatory, setSelectedSignatory] = useState<Signatory | null>(null);
    const { data, mutate: mutateSignatoriesList } = useGetSignatoriesList(studyId, module, true);
    const [signatories, setSignatories] = useState<Signatory[] | undefined>(undefined);
    const [sort, setSort] = useState<SortDescriptor[]>([]);

    useEffect(() => {
        if (data) {
            setSignatories(signatoriesGridColumnHelper(data, prefixesData?.auditRecordIdPrefixes));
        }
    }, [data]);

    const showManageSignatory = useBoolean(false);
    const showDeleteSignatory = useBoolean(false);

    let moduleName = '';
    switch (module) {
        case 2:
            moduleName = 'RAM';
            break;
        case 8:
            moduleName = 'Central Monitoring';
            break;
        case 16:
            moduleName = 'Subject Monitoring';
            break;
        default:
            throw new Error(`Module ${module} not implemented`);
    }
    const exportFileName = `${moduleName} Signatories_${studyName}_${stringDateWithoutTimeFromUtc(
        new Date(),
        'DD-MMM-YYYY',
    )}`;

    const columns = useMemo(() => MANAGE_SIGNATORIES_GRID_COLUMNS(t, customCells), [module]);

    useEffect(() => {
        setSelectedSignatory(selectedSignatory);
    }, [selectedSignatory]);

    const setSelectedRow = async (success: boolean | undefined, id: string | undefined) => {
        if (success) {
            const mutatedList = await mutateSignatoriesList();

            if (id && mutatedList) {
                const signatory = mutatedList?.find((s) => s.id === id);
                setSelectedSignatory(signatory ?? null);
            }
        }
    };

    const onDeleteClose = () => {
        showDeleteSignatory.setFalse();
        setSelectedSignatory(null);
    };
    const onDelete = async (success: boolean | undefined) => {
        if (success) {
            await mutateSignatoriesList();
        }
        onDeleteClose();
    };

    const onAddOrEditClose = () => {
        showManageSignatory.setFalse();
    };
    const onAddOrEdit = async (success: boolean | undefined, id: string | undefined) => {
        await setSelectedRow(success, id);

        onAddOrEditClose();
    };

    if (isLoadingPrefixes) return <Loading />;

    return (
        <div className="k-h-full">
            {showManageSignatory.value && (
                <ManageSignatoryDialog
                    onClose={onAddOrEditClose}
                    signatory={selectedSignatory}
                    onSubmit={onAddOrEdit}
                    type="edit"
                    module={module}
                />
            )}
            {showDeleteSignatory.value && selectedSignatory && (
                <DeleteSignatoryDialog
                    onClose={onDeleteClose}
                    signatory={selectedSignatory}
                    onDeleteHandler={onDelete}
                    module={module}
                />
            )}
            <Grid
                title={t('cm:signatories')}
                maxHeight="100%"
                testDataId="cm2-manage-signatories-grid"
                data={orderBy(signatories || [], sort)}
                showPlaceholder={false}
                exportFilename={exportFileName}
                service={mapService(module)}
                area={mapApplicationArea(module)}
                dataType={AuditDataType.EXCEL_EXPORT}
                selectable={{
                    enabled: true,
                    mode: 'single',
                }}
                sort={sort}
                onSortChange={(e: GridSortChangeEvent) => {
                    setSort(e.sort);
                }}
                sortable
                dataItemKey="id"
                defaultSelectedItem={selectedSignatory ?? undefined}
                showAddIcon
                addRecordTooltip={t('cm:addSignatory')}
                addItemDialog={(onSuccessClear, onCancel) => (
                    <ManageSignatoryDialog
                        onClose={onCancel}
                        signatory={null}
                        onSubmit={async (success, id) => {
                            onSuccessClear(true, true); // clear filters and sort
                            await setSelectedRow(success, id);
                            onCancel();
                        }}
                        type="add"
                        module={module}
                    />
                )}
                canEdit={canEdit}
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('reports:editSignatory'),
                        onClick: (dataItem: unknown) => {
                            const signatory = dataItem as Signatory;
                            setSelectedSignatory(signatory);
                            showManageSignatory.setTrue();
                        },
                        icon: ['far', 'pen-to-square'],
                        disabled: !canEdit,
                    },
                    {
                        id: 2,
                        label: t('reports:deleteSignatory'),
                        onClick: (dataItem: unknown) => {
                            const signatory = dataItem as Signatory;
                            setSelectedSignatory(signatory);
                            showDeleteSignatory.setTrue();
                        },
                        icon: ['fas', 'trash-xmark'],
                        disabled: !canEdit,
                        colorProp: 'Red',
                    },
                ]}
                columns={columns}
            />
        </div>
    );
};

export default ManageSignatories;
